/**
 * The pages a URL names by a flag (`?assets`), and the invite code it carries.
 *
 * The folder Workshop (`?workshop`, no login) retired into the Assets page in
 * assets stage 4; its flag survives as an alias, so an old bookmark lands on
 * the Assets page (after sign-in) rather than nowhere.
 */
import { useState, useEffect } from 'react';

/**
 * The query string with a page flag set or cleared, and its old `alias`
 * cleared either way; null when it already says so.
 */
export function flagQuery(search, on, flag, alias) {
  const params = new URLSearchParams(search);
  if (on === params.has(flag) && !(alias && params.has(alias))) return null;
  if (alias) params.delete(alias);
  if (on) params.set(flag, '');
  else params.delete(flag);
  return params.toString();
}

/** The URL `?join=CODE` carries - the invite link - upper-cased, six characters at most. */
export function joinCodeFromUrl() {
  if (typeof window === 'undefined') return '';
  return (new URLSearchParams(window.location.search).get('join') ?? '').toUpperCase().slice(0, 6);
}

/**
 * A page the URL names by a flag - `?assets` (the GM's library, v11.68.0) -
 * read once and written back on every change, so it is bookmarkable; an old
 * `alias` in the URL opens it too and is rewritten to the flag. Returns `[on, setOn]`.
 */
export function useUrlFlag(flag, alias) {
  const [on, setOn] = useState(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return params.has(flag) || (!!alias && params.has(alias));
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const q = flagQuery(window.location.search, on, flag, alias);
    if (q === null) return;
    window.history.replaceState(null, '', q ? '?' + q : window.location.pathname);
  }, [on, flag, alias]);
  return [on, setOn];
}
